import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "../contextapi/SettingsContext";
import { toast } from "react-toastify";
import { formatPriceAmount } from "../utils/saleUtils";
import {
  DEFAULT_HERO_CONTENT,
  HERO_LIMITS,
  isValidHeroColor,
  isValidHeroCtaUrl,
  normalizeHeroColor,
} from "../constants/heroContent";
import defaultHero from "../assets/images/hero.jpg";
import HeroSection from "../web-pages/HeroSection";
import "./Settings.css";

const heroFromSettings = (settings = {}) => ({
  headline: settings.heroHeadline || DEFAULT_HERO_CONTENT.headline,
  body: settings.heroBody ?? DEFAULT_HERO_CONTENT.body,
  headlineColor: normalizeHeroColor(settings.heroHeadlineColor),
  ctaLabel: settings.heroCtaLabel || "",
  ctaUrl: settings.heroCtaUrl || "",
});

const sameText = (a, b) => String(a ?? "") === String(b ?? "");

const toColorInputValue = (hex) => {
  const value = normalizeHeroColor(hex);
  if (value.length === 4) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return value;
};

const Settings = () => {
  const { settings, updateSettings, loading } = useSettings();
  const heroInputRef = useRef(null);
  const [localMinimumPrice, setLocalMinimumPrice] = useState(
    settings.minimumPrice
  );
  const [heroForm, setHeroForm] = useState(() => heroFromSettings(settings));
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(
    settings.heroSectionImageUrl || ""
  );
  const [heroLoadFailed, setHeroLoadFailed] = useState(false);

  useEffect(() => {
    setLocalMinimumPrice(settings.minimumPrice);
    setHeroForm(heroFromSettings(settings));
  }, [
    settings.minimumPrice,
    settings.heroHeadline,
    settings.heroBody,
    settings.heroHeadlineColor,
    settings.heroCtaLabel,
    settings.heroCtaUrl,
  ]);

  useEffect(() => {
    if (!heroFile) {
      setHeroPreview(settings.heroSectionImageUrl || "");
      setHeroLoadFailed(false);
    }
  }, [settings.heroSectionImageUrl, heroFile]);

  const savedHero = useMemo(() => heroFromSettings(settings), [settings]);

  const handleHeroFieldChange = (field) => (e) => {
    const value = e.target.value;
    setHeroForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e) => {
    let value = e.target.value.trim();

    if (value === "") {
      setLocalMinimumPrice("");
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    if (/^0\d/.test(value)) {
      return;
    }

    const parts = value.split(".");
    if (parts.length > 2) {
      return;
    }
    if (parts[1] != null && parts[1].length > 2) {
      value = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    const num = parseFloat(value);
    if (!Number.isNaN(num) && num > 1000) {
      return;
    }

    setLocalMinimumPrice(value);
  };

  const handleSave = async () => {
    const n = localMinimumPrice === "" ? NaN : parseFloat(localMinimumPrice);
    if (localMinimumPrice === "" || Number.isNaN(n)) {
      toast.error("Enter a valid minimum price");
      return;
    }

    if (n < 0) {
      toast.error("Minimum price cannot be negative");
      return;
    }

    if (n > 1000) {
      toast.error("Minimum price cannot exceed $1000");
      return;
    }

    const headline = heroForm.headline.trim();
    const body = heroForm.body;
    const ctaLabel = heroForm.ctaLabel.trim();
    const ctaUrl = heroForm.ctaUrl.trim();
    const headlineColor = heroForm.headlineColor.trim();

    if (!headline) {
      toast.error("Hero headline is required");
      return;
    }
    if (headline.length > HERO_LIMITS.headline) {
      toast.error(`Headline cannot exceed ${HERO_LIMITS.headline} characters`);
      return;
    }
    if (body.length > HERO_LIMITS.body) {
      toast.error(`Hero text cannot exceed ${HERO_LIMITS.body} characters`);
      return;
    }
    if (!isValidHeroColor(headlineColor)) {
      toast.error("Enter a valid headline color like #F0AB73");
      return;
    }
    if (ctaLabel.length > HERO_LIMITS.ctaLabel) {
      toast.error(`Button label cannot exceed ${HERO_LIMITS.ctaLabel} characters`);
      return;
    }
    if (ctaUrl.length > HERO_LIMITS.ctaUrl) {
      toast.error(`Button URL cannot exceed ${HERO_LIMITS.ctaUrl} characters`);
      return;
    }
    if (Boolean(ctaLabel) !== Boolean(ctaUrl)) {
      toast.error("Add both a button label and URL, or leave both empty to hide the button");
      return;
    }
    if (!isValidHeroCtaUrl(ctaUrl)) {
      toast.error("Use an internal path like /shop or a full http(s) URL");
      return;
    }

    const result = await updateSettings({
      minimumPrice: n,
      heroHeadline: headline,
      heroBody: body,
      heroHeadlineColor: normalizeHeroColor(headlineColor),
      heroCtaLabel: ctaLabel,
      heroCtaUrl: ctaUrl,
      ...(heroFile ? { heroSectionImage: heroFile } : {}),
    });

    if (result.success) {
      if (heroPreview?.startsWith("blob:")) URL.revokeObjectURL(heroPreview);
      setHeroFile(null);
      if (heroInputRef.current) heroInputRef.current.value = "";
      toast.success("Settings updated successfully!");
    } else {
      toast.error(
        "Failed to update settings: " + (result.error || "Unknown error")
      );
    }
  };

  const handleHeroChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (heroPreview?.startsWith("blob:")) URL.revokeObjectURL(heroPreview);
    setHeroFile(file);
    setHeroLoadFailed(false);
    setHeroPreview(URL.createObjectURL(file));
  };

  const clearHeroSelection = () => {
    if (heroPreview?.startsWith("blob:")) URL.revokeObjectURL(heroPreview);
    setHeroFile(null);
    setHeroPreview(settings.heroSectionImageUrl || "");
    setHeroLoadFailed(false);
    if (heroInputRef.current) heroInputRef.current.value = "";
  };

  const handleReset = () => {
    setLocalMinimumPrice(settings.minimumPrice);
    setHeroForm(heroFromSettings(settings));
    clearHeroSelection();
  };

  const handleRestoreDefaultHero = () => {
    setHeroForm({ ...DEFAULT_HERO_CONTENT });
  };

  const priceChanged = (() => {
    const saved = Number(settings?.minimumPrice ?? 0);
    if (localMinimumPrice === "") {
      return saved !== 0;
    }
    const n = parseFloat(localMinimumPrice);
    if (Number.isNaN(n)) return true;
    return Math.round(n * 100) !== Math.round(saved * 100);
  })();

  const heroContentChanged =
    !sameText(heroForm.headline.trim(), savedHero.headline.trim()) ||
    !sameText(heroForm.body, savedHero.body) ||
    normalizeHeroColor(heroForm.headlineColor) !==
      normalizeHeroColor(savedHero.headlineColor) ||
    !sameText(heroForm.ctaLabel.trim(), savedHero.ctaLabel.trim()) ||
    !sameText(heroForm.ctaUrl.trim(), savedHero.ctaUrl.trim());

  const hasChanges = priceChanged || Boolean(heroFile) || heroContentChanged;
  const savedHeroUrl = settings.heroSectionImageUrl || "";
  const previewSrc = heroPreview || defaultHero;
  const usingDefaultHero = !heroFile && (!savedHeroUrl || heroLoadFailed);

  const isDefaultCopy =
    sameText(heroForm.headline.trim(), DEFAULT_HERO_CONTENT.headline) &&
    sameText(heroForm.body, DEFAULT_HERO_CONTENT.body) &&
    normalizeHeroColor(heroForm.headlineColor) ===
      DEFAULT_HERO_CONTENT.headlineColor &&
    !heroForm.ctaLabel.trim() &&
    !heroForm.ctaUrl.trim();

  const previewCtaLabel = heroForm.ctaLabel.trim();
  const previewCtaUrl = heroForm.ctaUrl.trim();
  const previewHasCta = Boolean(previewCtaLabel && previewCtaUrl);

  return (
    <div className="admin-settings">
      <header className="admin-settings__header">
        <p className="admin-settings__eyebrow">Admin</p>
        <h2 className="admin-settings__title">Site settings</h2>
        <p className="admin-settings__subtitle">
          Control the lowest mockup price sellers can set and the homepage hero
          visitors see first. The layout stays on the site; you only edit the
          content.
        </p>
      </header>

      <div className="admin-settings__grid">
        <section className="admin-settings__card">
          <div className="admin-settings__card-head">
            <span className="admin-settings__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2v20M17 7.5c0-1.9-2.2-3.5-5-3.5S7 5.6 7 7.5 9.2 11 12 11s5 1.6 5 3.5-2.2 3.5-5 3.5-5-1.6-5-3.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div>
              <h2>Minimum listing price</h2>
              <p>Sellers cannot publish mockups below this amount.</p>
            </div>
          </div>
          <div className="admin-settings__card-body">
            <label className="admin-settings__label" htmlFor="minimum-price">
              Minimum price
            </label>
            <div className="admin-settings__price-field">
              <span className="admin-settings__currency">USD</span>
              <input
                id="minimum-price"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={localMinimumPrice === "" ? "" : localMinimumPrice}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </div>
            <p className="admin-settings__hint">
              Use 0 to allow free mockups. Maximum is $1000, up to 2 decimal
              places.
            </p>

            <div className="admin-settings__summary">
              <div className="admin-settings__stat">
                <span>Active minimum</span>
                <strong>{formatPriceAmount(settings.minimumPrice)}</strong>
              </div>
              <div className="admin-settings__stat">
                <span>Hero image</span>
                <span
                  className={`admin-settings__badge${
                    savedHeroUrl ? "" : " admin-settings__badge--muted"
                  }`}
                >
                  {savedHeroUrl ? "Custom" : "Default"}
                </span>
              </div>
              <div className="admin-settings__stat">
                <span>Hero button</span>
                <span
                  className={`admin-settings__badge${
                    savedHero.ctaLabel && savedHero.ctaUrl
                      ? ""
                      : " admin-settings__badge--muted"
                  }`}
                >
                  {savedHero.ctaLabel && savedHero.ctaUrl ? "Visible" : "Hidden"}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="admin-settings__card">
          <div className="admin-settings__card-head">
            <span className="admin-settings__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <circle cx="8.5" cy="10" r="1.5" fill="currentColor" />
                <path
                  d="M7 17l4.2-4.2a1 1 0 0 1 1.4 0L21 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <div>
              <h2>Homepage hero image</h2>
              <p>Shown on the homepage hero. A wide photo works best.</p>
            </div>
          </div>
          <div className="admin-settings__card-body">
            <div
              className="admin-settings__hero"
              role="button"
              tabIndex={0}
              onClick={() => heroInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  heroInputRef.current?.click();
                }
              }}
            >
              <img
                src={previewSrc}
                alt="Homepage hero"
                referrerPolicy="no-referrer"
                onError={() => {
                  if (!heroPreview?.startsWith("blob:")) {
                    setHeroLoadFailed(true);
                    setHeroPreview("");
                  }
                }}
              />
              <div className="admin-settings__hero-overlay">
                <span>{heroFile ? "New image selected" : "Change image"}</span>
                <em>{usingDefaultHero ? "Default hero" : "Current hero"}</em>
              </div>
            </div>

            <input
              ref={heroInputRef}
              type="file"
              accept="image/*"
              className="d-none"
              onChange={handleHeroChange}
            />

            <div className="admin-settings__actions-row">
              <button
                type="button"
                className="btn grey-btn"
                onClick={() => heroInputRef.current?.click()}
                disabled={loading}
              >
                {savedHeroUrl || heroFile ? "Replace image" : "Upload image"}
              </button>
              {heroFile && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={clearHeroSelection}
                  disabled={loading}
                >
                  Clear selection
                </button>
              )}
            </div>
            {heroFile ? (
              <p className="admin-settings__file-name">{heroFile.name}</p>
            ) : heroLoadFailed && savedHeroUrl ? (
              <p className="admin-settings__file-name">
                Saved image could not be loaded. Replace it or check the file
                URL.
              </p>
            ) : null}
          </div>
        </section>
      </div>

      <section className="admin-settings__card admin-settings__card--hero">
        <div className="admin-settings__card-head">
          <span className="admin-settings__icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 7h16M4 12h10M4 17h7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <div>
            <h2>Homepage hero content</h2>
            <p>
              Edit the headline, text, and optional button. Leave the button
              label empty to hide it.
            </p>
          </div>
        </div>
        <div className="admin-settings__hero-editor">
          <div className="admin-settings__hero-fields">
            <div className="admin-settings__field">
              <label className="admin-settings__label" htmlFor="hero-headline">
                Headline
              </label>
              <input
                id="hero-headline"
                className="admin-settings__text-input"
                type="text"
                maxLength={HERO_LIMITS.headline}
                value={heroForm.headline}
                onChange={handleHeroFieldChange("headline")}
                placeholder={DEFAULT_HERO_CONTENT.headline}
              />
              <p className="admin-settings__hint">
                {heroForm.headline.trim().length}/{HERO_LIMITS.headline}
              </p>
            </div>

            <div className="admin-settings__field">
              <label className="admin-settings__label" htmlFor="hero-body">
                Body text
              </label>
              <textarea
                id="hero-body"
                className="admin-settings__textarea"
                rows={7}
                maxLength={HERO_LIMITS.body}
                value={heroForm.body}
                onChange={handleHeroFieldChange("body")}
                placeholder={DEFAULT_HERO_CONTENT.body}
              />
              <p className="admin-settings__hint">
                Line breaks are kept. {heroForm.body.length}/{HERO_LIMITS.body}
              </p>
            </div>

            <div className="admin-settings__field">
              <label className="admin-settings__label" htmlFor="hero-color">
                Headline color
              </label>
              <div className="admin-settings__color-field">
                <input
                  type="color"
                  aria-label="Pick headline color"
                  value={toColorInputValue(heroForm.headlineColor)}
                  onChange={handleHeroFieldChange("headlineColor")}
                />
                <input
                  id="hero-color"
                  className="admin-settings__text-input"
                  type="text"
                  maxLength={7}
                  value={heroForm.headlineColor}
                  onChange={handleHeroFieldChange("headlineColor")}
                  placeholder="#F0AB73"
                />
              </div>
            </div>

            <div className="admin-settings__cta-grid">
              <div className="admin-settings__field">
                <label className="admin-settings__label" htmlFor="hero-cta-label">
                  Button label
                </label>
                <input
                  id="hero-cta-label"
                  className="admin-settings__text-input"
                  type="text"
                  maxLength={HERO_LIMITS.ctaLabel}
                  value={heroForm.ctaLabel}
                  onChange={handleHeroFieldChange("ctaLabel")}
                  placeholder="Browse mockups"
                />
              </div>
              <div className="admin-settings__field">
                <label className="admin-settings__label" htmlFor="hero-cta-url">
                  Button URL
                </label>
                <input
                  id="hero-cta-url"
                  className="admin-settings__text-input"
                  type="text"
                  maxLength={HERO_LIMITS.ctaUrl}
                  value={heroForm.ctaUrl}
                  onChange={handleHeroFieldChange("ctaUrl")}
                  placeholder="/shop"
                />
              </div>
            </div>
            <p className="admin-settings__hint">
              Internal path like <code>/shop</code> or a full URL like{" "}
              <code>https://example.com</code>. Leave both empty to hide the
              button.
            </p>

            <div className="admin-settings__actions-row">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleRestoreDefaultHero}
                disabled={loading || isDefaultCopy}
              >
                Restore default copy
              </button>
            </div>
          </div>

          <div className="admin-settings__hero-preview-wrap">
            <div className="admin-settings__preview-label">Live preview</div>
            <HeroSection
              preview
              headline={heroForm.headline.trim() || DEFAULT_HERO_CONTENT.headline}
              body={heroForm.body}
              headlineColor={
                isValidHeroColor(heroForm.headlineColor)
                  ? normalizeHeroColor(heroForm.headlineColor)
                  : DEFAULT_HERO_CONTENT.headlineColor
              }
              imageSrc={previewSrc}
              ctaLabel={previewHasCta ? previewCtaLabel : ""}
              ctaUrl={previewHasCta ? previewCtaUrl : ""}
            />
            <p className="admin-settings__hint">
              This is the homepage layout. Save to publish it for visitors.
            </p>
          </div>
        </div>
      </section>

      <div className="admin-settings__footer">
        <p className="admin-settings__footer-copy">
          {hasChanges ? (
            <>
              You have <strong>unsaved changes</strong>
            </>
          ) : (
            "No pending changes"
          )}
        </p>
        <div className="admin-settings__footer-actions">
          {hasChanges && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            className="btn red-btn"
            onClick={handleSave}
            disabled={!hasChanges || loading}
          >
            {loading ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
